import {
  RouteRecordRaw,
  RouteLocationNormalized,
  LocationQueryRaw,
} from "vue-router";
import { SessionStorageEnum } from "@/apps/merchant3/enums/sessionStorageEnum";
import { OriginationChannelEnum } from "@/apps/merchant3/enums/originationChannelEnum";
import { getCountryCode as getCentralizedCountryCode } from "@/apps/merchant3/helpers/country";
import { ProductVersion } from "@/apps/merchant3/types/ProductVersion";

/**
 * Gets country code from URL params with centralized fallback
 * URL param check is kept here because legacy redirects translate old URLs
 * (e.g., /d2c/:countryCode) to new structure -- the old URL IS the input being translated
 */
const getCountryCode = (to: RouteLocationNormalized): string => {
  if (to.params.countryCode) {
    return (to.params.countryCode as string).toLowerCase();
  }
  return getCentralizedCountryCode();
};

/**
 * Determines the flow type based on session state
 */
const getFlowType = (): string => {
  // Check for D2C (Direct to Customer)
  const originationChannel = sessionStorage.getItem(
    SessionStorageEnum.OriginationChannel
  );
  if (originationChannel === OriginationChannelEnum.DirectToCustomer) {
    return "d2c";
  }

  // NOTE: Kiosk flow intentionally NOT checked here.
  // Kiosk-specific routes (/kiosk/instructions, /kiosk/summary, etc.) are
  // navigated to directly, not through legacy redirects. Customer registration
  // routes should use /store/ path even in kiosk mode.

  // Check for ecommerce via product version
  const productVersion = sessionStorage.getItem(
    SessionStorageEnum.CartProductVersion
  );
  if (productVersion === ProductVersion.M3_ECOMMERCE) {
    return "ecommerce";
  }

  // Default to store (standard M3 flow)
  return "store";
};

/**
 * Legacy redirect routes for backwards compatibility
 * These redirect old /d2c/* and /merchant3/* URLs to the new /:country/:flow/* structure
 */
export const legacyRedirects: Array<RouteRecordRaw> = [
  // ===== D2C Legacy Redirects =====
  // Old D2C entry point: /d2c/:countryCode -> /:countryCode/d2c
  {
    path: "/d2c/:countryCode([A-Za-z]{2})",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/d2c`,
      query: to.query,
    }),
  },
  // Old D2C offer summary: /d2c/offer-summary -> /:countryCode/d2c/offer-summary
  {
    path: "/d2c/offer-summary",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/d2c/offer-summary`,
      query: to.query,
    }),
  },
  // Old D2C terms: /d2c/customer-registration/terms-conditions -> /:countryCode/d2c/customer-registration/terms-conditions
  {
    path: "/d2c/customer-registration/terms-conditions",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/d2c/customer-registration/terms-conditions`,
      query: to.query,
    }),
  },

  // ===== Ecommerce Legacy Redirects =====
  // These routes specifically go to ecommerce flow
  {
    path: "/merchant3/ecommerce-start",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/ecommerce/start`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/ecommerce/rejected",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/ecommerce/rejected`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/ecommerce/summary",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/ecommerce/summary`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/ecommerce/selectDownpaymentType",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/ecommerce/selectDownpaymentType`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/ecommerce/card-ui-redirect",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/ecommerce/card-ui-redirect`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/ecommerce/cashInStoreDownpayment",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/ecommerce/cashInStoreDownpayment`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/ecommerce-openpay-down-payment",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/ecommerce/openpay-down-payment`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/ecommerce-down-payment-paid",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/ecommerce/down-payment-paid`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/ecommerce-partner-down-payment",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/ecommerce/partner-down-payment`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/ecommerce-pix-down-payment",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/ecommerce/pix-down-payment`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/ecommerce-finished-sale",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/ecommerce/finished-sale`,
      query: to.query,
    }),
  },

  // ===== Self-Onboarding/Kiosk Legacy Redirects =====
  {
    path: "/merchant3/self-onboarding/instructions",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/kiosk/instructions`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/self-onboarding/summary",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/kiosk/summary`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/self-onboarding/get-valid-cart",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/kiosk/get-valid-cart`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/self-onboarding/cart-details",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/kiosk/cart-details`,
      query: to.query,
    }),
  },

  // ===== Standard M3 Legacy Redirects =====
  // Specific routes first (to avoid catch-all matching)
  {
    path: "/merchant3/merchant-login",
    redirect: (to) => ({
      path: `/store/merchant-login`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/clerk-login",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/store/clerk-login`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/qr-login",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/store/qr-login`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/device-login",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/store/device-login`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/qr",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/store/qr`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/offers",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/${getFlowType()}/offers`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/imei",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/${getFlowType()}/imei`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/contract",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/${getFlowType()}/contract`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/cart-verification",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/${getFlowType()}/cart-verification`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/software",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/${getFlowType()}/software`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/complete",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/${getFlowType()}/complete`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/customer-registration",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/${getFlowType()}/customer-registration`,
      query: to.query,
    }),
  },

  // ===== Catch-all for remaining /merchant3/* routes =====
  // This must be last to not interfere with specific redirects above
  {
    path: "/merchant3/:pathMatch(.*)*",
    redirect: (
      to: RouteLocationNormalized
    ): { path: string; query: LocationQueryRaw } => {
      const subPath = Array.isArray(to.params.pathMatch)
        ? to.params.pathMatch.join("/")
        : (to.params.pathMatch as string) || "";

      // Routes that only exist under /store/ should always use /store/
      // This includes customer-registration/* and step/* routes
      // (D2C only has explicit route for terms-conditions, not other customer-registration pages)
      const storeOnlyPrefixes = ["customer-registration/", "step/"];
      const shouldUseStore = storeOnlyPrefixes.some((prefix) =>
        subPath.startsWith(prefix)
      );
      const flow = shouldUseStore ? "store" : getFlowType();

      return {
        path: `/${getCountryCode(to)}/${flow}/${subPath}`,
        query: to.query,
      };
    },
  },

  // Base merchant3 route (welcome page)
  {
    path: "/merchant3",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/store/welcome`,
      query: to.query,
    }),
  },

  // ===== Configurable Step Routes =====
  // These routes only exist under /store/, so always use /store/
  {
    path: "/merchant3/step/policy",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/store/step/policy`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/step/kyc",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/store/step/kyc`,
      query: to.query,
    }),
  },
  {
    path: "/merchant3/step/credit-check",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/store/step/credit-check`,
      query: to.query,
    }),
  },

  // ===== Bare Store Path Redirect =====
  // Handles navigation to "/:countryCode/store" (without sub-path) by redirecting to welcome
  {
    path: "/:countryCode/store",
    redirect: (to) => ({
      path: `/${to.params.countryCode}/store/welcome`,
      query: to.query,
    }),
  },

  // ===== Root Path Redirect =====
  // Handles navigation to "/" by redirecting to the default welcome page
  {
    path: "/",
    redirect: (to) => ({
      path: `/${getCountryCode(to)}/store/welcome`,
      query: to.query,
    }),
  },
];
